import { useCallback, useEffect, useState } from 'react'

/**
 * Winziger Router auf Basis der History-API.
 *
 * Die App hat vier Routen; eine Router-Bibliothek wäre hier mehr Abhängigkeit
 * als Nutzen. Reicht das nicht mehr, ist der Austausch auf diese Datei begrenzt.
 */

type Listener = () => void
const listeners = new Set<Listener>()

function notify(): void {
  for (const listener of listeners) listener()
}

export function navigate(path: string, replace = false): void {
  if (replace) window.history.replaceState(null, '', path)
  else window.history.pushState(null, '', path)
  notify()
}

export function usePath(): string {
  const [path, setPath] = useState(() => window.location.pathname)

  useEffect(() => {
    const update = (): void => setPath(window.location.pathname)
    listeners.add(update)
    window.addEventListener('popstate', update)
    return () => {
      listeners.delete(update)
      window.removeEventListener('popstate', update)
    }
  }, [])

  return path
}

/** `/b/:id` → `['b', ':id']`, gefiltert um leere Segmente. */
export function useSegments(): string[] {
  const path = usePath()
  return path.split('/').filter(Boolean)
}

export function useNavigate(): (path: string, replace?: boolean) => void {
  return useCallback((path: string, replace?: boolean) => navigate(path, replace), [])
}

/** Interner Link, der keinen Full-Page-Reload auslöst. */
export function Link({
  to,
  children,
  ...rest
}: { to: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>): React.ReactElement {
  return (
    <a
      href={to}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        navigate(to)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
